import { useState, useRef, useEffect, ReactNode, useCallback } from "react";

interface AdminEditFieldProps {
  value: string;
  onSave: (val: string) => void;
  multiline?: boolean;
  className?: string;
  inputClassName?: string;
  placeholder?: string;
  label?: string;
  children?: ReactNode;
  richText?: boolean;
  onDelete?: () => void;
}

export default function AdminEditField({
  value,
  onSave,
  multiline = false,
  className = "",
  inputClassName = "",
  placeholder = "Enter text...",
  label,
  children,
  richText = false,
  onDelete,
}: AdminEditFieldProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [showLinkInput, setShowLinkInput] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");
  const [savedSelection, setSavedSelection] = useState<Range | null>(null);
  const inputRef = useRef<HTMLInputElement & HTMLTextAreaElement>(null);
  const richRef = useRef<HTMLDivElement>(null);
  const linkInputRef = useRef<HTMLInputElement>(null);
  const lastSelectionRef = useRef<Range | null>(null);

  useEffect(() => {
    if (!editing) setDraft(value);
  }, [value, editing]);

  const handleOpen = useCallback((e: React.MouseEvent) => {
    // Prevent parent AdminEditFields from also opening.
    // Each field's onClick calls stopPropagation so only the innermost
    // field (the one directly containing the click target) opens.
    e.stopPropagation();
    setDraft(value);
    setEditing(true);
    if (richText) {
      setTimeout(() => {
        if (richRef.current) {
          richRef.current.innerHTML = value || "";
          richRef.current.focus();
          const range = document.createRange();
          const sel = window.getSelection();
          range.selectNodeContents(richRef.current);
          range.collapse(false);
          sel?.removeAllRanges();
          sel?.addRange(range);
        }
      }, 30);
    } else {
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
          const len = inputRef.current.value.length;
          inputRef.current.setSelectionRange(len, len);
        }
      }, 50);
    }
  }, [value, richText]);

  const handleSave = useCallback((e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (richText && richRef.current) {
      onSave(richRef.current.innerHTML);
    } else {
      onSave(draft);
    }
    setEditing(false);
    setShowLinkInput(false);
  }, [richText, draft, onSave]);

  const handleCancel = useCallback((e?: React.MouseEvent) => {
    e?.stopPropagation();
    setEditing(false);
    setDraft(value);
    setShowLinkInput(false);
  }, [value]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (!multiline && !richText && e.key === "Enter") {
      e.preventDefault();
      handleSave();
    }
    if (e.key === "Escape") handleCancel();
    if ((e.ctrlKey || e.metaKey) && e.key === "s") {
      e.preventDefault();
      handleSave();
    }
  }, [multiline, richText, handleSave, handleCancel]);

  // Rich text format commands
  const execFormat = useCallback((cmd: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    richRef.current?.focus();
    document.execCommand(cmd, false);
  }, []);

  const handleEditorSelectionChange = useCallback(() => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0 && richRef.current?.contains(sel.anchorNode)) {
      lastSelectionRef.current = sel.getRangeAt(0).cloneRange();
    }
  }, []);

  const handleLinkButtonClick = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const sel = window.getSelection();
    let range: Range | null = null;
    if (sel && sel.rangeCount > 0 && richRef.current?.contains(sel.anchorNode)) {
      range = sel.getRangeAt(0).cloneRange();
    } else if (lastSelectionRef.current) {
      range = lastSelectionRef.current;
    }
    if (range) setSavedSelection(range);
    setShowLinkInput(!showLinkInput);
    setLinkUrl("");
    setTimeout(() => linkInputRef.current?.focus(), 50);
  }, [showLinkInput]);

  const insertLink = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!linkUrl.trim()) {
      setShowLinkInput(false);
      return;
    }
    richRef.current?.focus();
    if (savedSelection) {
      const sel = window.getSelection();
      sel?.removeAllRanges();
      sel?.addRange(savedSelection);
    }
    const url =
      linkUrl.startsWith("http") ||
      linkUrl.startsWith("mailto:") ||
      linkUrl.startsWith("/")
        ? linkUrl
        : `https://${linkUrl}`;
    document.execCommand("createLink", false, url);
    if (richRef.current) {
      richRef.current.querySelectorAll("a").forEach((a) => {
        if (!a.getAttribute("target")) {
          a.setAttribute("target", "_blank");
          a.setAttribute("rel", "noopener noreferrer");
        }
      });
    }
    setShowLinkInput(false);
    setLinkUrl("");
    setSavedSelection(null);
  }, [linkUrl, savedSelection]);

  const removeLink = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    richRef.current?.focus();
    document.execCommand("unlink", false);
    setShowLinkInput(false);
  }, []);

  // ── Editing mode ──────────────────────────────────────────────────────────
  if (editing) {
    return (
      <div className={`relative ${className}`} onClick={(e) => e.stopPropagation()}>
        {label && (
          <div className="flex items-center justify-between mb-1">
            <div className="text-xs text-gray-400 font-semibold uppercase tracking-widest">
              {label}
            </div>
            {onDelete && (
              <button
                onClick={(e) => { e.stopPropagation(); onDelete(); }}
                className="w-6 h-6 flex items-center justify-center rounded-full bg-red-500 hover:bg-red-600 text-white cursor-pointer transition-colors"
                title="Remove"
              >
                <i className="ri-close-line text-xs"></i>
              </button>
            )}
          </div>
        )}

        {!label && onDelete && (
          <div className="flex items-center justify-end mb-1">
            <button
              onClick={(e) => { e.stopPropagation(); onDelete(); }}
              className="w-6 h-6 flex items-center justify-center rounded-full bg-red-500 hover:bg-red-600 text-white cursor-pointer transition-colors"
              title="Remove"
            >
              <i className="ri-close-line text-xs"></i>
            </button>
          </div>
        )}

        {richText ? (
          <div className="border-2 border-pink-300 rounded-lg overflow-hidden bg-white focus-within:ring-2 focus-within:ring-pink-200">
            <div className="flex items-center gap-0.5 px-2 py-1.5 border-b border-pink-100 bg-pink-50 flex-wrap">
              <button
                onMouseDown={(e) => execFormat("bold", e)}
                className="w-7 h-7 flex items-center justify-center rounded hover:bg-pink-200 text-gray-700 font-bold text-sm cursor-pointer transition-colors"
                title="Bold (Ctrl+B)"
              >
                B
              </button>
              <button
                onMouseDown={(e) => execFormat("italic", e)}
                className="w-7 h-7 flex items-center justify-center rounded hover:bg-pink-200 text-gray-700 italic text-sm cursor-pointer transition-colors"
                title="Italic (Ctrl+I)"
              >
                I
              </button>
              <button
                onMouseDown={(e) => execFormat("underline", e)}
                className="w-7 h-7 flex items-center justify-center rounded hover:bg-pink-200 text-gray-700 underline text-sm cursor-pointer transition-colors"
                title="Underline (Ctrl+U)"
              >
                U
              </button>
              <div className="w-px h-4 bg-pink-200 mx-1"></div>
              <button
                onMouseDown={handleLinkButtonClick}
                className={`w-7 h-7 flex items-center justify-center rounded text-sm cursor-pointer transition-colors ${showLinkInput ? "bg-pink-500 text-white" : "hover:bg-pink-200 text-gray-600"}`}
                title="Insert link"
              >
                <i className="ri-link text-xs"></i>
              </button>
              <button
                onMouseDown={removeLink}
                className="w-7 h-7 flex items-center justify-center rounded hover:bg-pink-200 text-gray-500 text-xs cursor-pointer transition-colors"
                title="Remove link"
              >
                <i className="ri-link-unlink text-xs"></i>
              </button>
              <div className="w-px h-4 bg-pink-200 mx-1"></div>
              <button
                onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); document.execCommand("removeFormat", false); richRef.current?.focus(); }}
                className="w-7 h-7 flex items-center justify-center rounded hover:bg-pink-200 text-gray-500 text-xs cursor-pointer transition-colors"
                title="Clear formatting"
              >
                <i className="ri-format-clear text-xs"></i>
              </button>
              <span className="ml-auto text-[10px] text-gray-400 pr-1 hidden sm:block">Ctrl+B/I/U</span>
            </div>
            {showLinkInput && (
              <div className="flex items-center gap-2 px-2 py-1.5 border-b border-pink-100 bg-pink-50/50">
                <i className="ri-link text-pink-400 text-xs flex-shrink-0"></i>
                <input
                  ref={linkInputRef}
                  type="text"
                  value={linkUrl}
                  onChange={(e) => setLinkUrl(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") { e.preventDefault(); insertLink(e as unknown as React.MouseEvent); }
                    if (e.key === "Escape") { setShowLinkInput(false); }
                  }}
                  placeholder="https://example.com or /page"
                  className="flex-1 text-xs border border-pink-200 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-pink-300 bg-white"
                />
                <button
                  onMouseDown={insertLink}
                  className="text-xs font-bold bg-pink-500 hover:bg-pink-600 text-white px-2.5 py-1 rounded-full cursor-pointer whitespace-nowrap transition-colors"
                >
                  Insert
                </button>
                <button
                  onMouseDown={(e) => { e.preventDefault(); setShowLinkInput(false); }}
                  className="text-xs text-gray-400 hover:text-gray-600 cursor-pointer whitespace-nowrap"
                >
                  Cancel
                </button>
              </div>
            )}
            <div
              ref={richRef}
              contentEditable
              suppressContentEditableWarning
              onKeyDown={handleKeyDown}
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
              onMouseUp={handleEditorSelectionChange}
              onKeyUp={handleEditorSelectionChange}
              onSelect={handleEditorSelectionChange}
              data-placeholder={placeholder}
              className={`min-h-[80px] px-3 py-2 text-sm text-gray-800 focus:outline-none empty:before:content-[attr(data-placeholder)] empty:before:text-gray-300 empty:before:italic ${inputClassName}`}
              style={{ userSelect: "text", WebkitUserSelect: "text" }}
            />
          </div>
        ) : multiline ? (
          <textarea
            ref={inputRef as React.RefObject<HTMLTextAreaElement>}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={handleKeyDown}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            rows={4}
            placeholder={placeholder}
            className={`w-full border-2 border-pink-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pink-200 resize-none bg-white text-gray-800 ${inputClassName}`}
            style={{ userSelect: "text", WebkitUserSelect: "text" }}
          />
        ) : (
          <input
            ref={inputRef as React.RefObject<HTMLInputElement>}
            type="text"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={handleKeyDown}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            placeholder={placeholder}
            className={`w-full border-2 border-pink-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pink-200 bg-white text-gray-800 ${inputClassName}`}
            style={{ userSelect: "text", WebkitUserSelect: "text" }}
          />
        )}

        <div className="flex gap-2 mt-1.5">
          <button
            onClick={handleSave}
            className="text-xs bg-green-500 hover:bg-green-600 text-white font-bold px-3 py-1 rounded-full cursor-pointer whitespace-nowrap"
          >
            Save
          </button>
          <button
            onClick={handleCancel}
            className="text-xs text-gray-400 hover:text-gray-600 cursor-pointer whitespace-nowrap"
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }

  // ── Display mode ────────────────────────────────────────────────────────────
  return (
    <div
      className={`group/field relative cursor-pointer ${className}`}
      onClick={handleOpen}
      title="Click to edit"
    >
      {/* Delete button */}
      {onDelete && (
        <button
          onClick={(e) => { e.stopPropagation(); onDelete(); }}
          className="absolute -top-2.5 -right-2.5 z-20 w-6 h-6 flex items-center justify-center bg-red-500 hover:bg-red-600 text-white rounded-full cursor-pointer shadow-sm transition-colors"
          title="Remove"
        >
          <i className="ri-close-line text-xs"></i>
        </button>
      )}

      {/* Edit badge (only if no delete button) */}
      {!onDelete && (
        <div className="absolute -top-2 -right-2 w-5 h-5 flex items-center justify-center bg-pink-500 rounded-full opacity-0 group-hover:opacity-100 transition-opacity z-20 pointer-events-none">
          <i className="ri-edit-line text-white" style={{ fontSize: "9px" }}></i>
        </div>
      )}

      {/* Hover outline */}
      <div className="absolute inset-0 rounded pointer-events-none border-2 border-transparent group-hover:border-pink-300 transition-all -m-0.5 z-[1]"></div>

      {/* Content — pointer-events-none on all interactive descendants
          so they don't capture clicks that should open edit mode */}
      {!value ? (
        <span className="inline-flex items-center gap-1.5 text-pink-400/80 italic text-sm bg-pink-500/10 rounded px-2 py-0.5 relative z-[2]">
          <i className="ri-add-line text-xs"></i>
          {placeholder}
        </span>
      ) : (
        <div className="relative z-[2] [&_a]:pointer-events-none [&_button]:pointer-events-none [&_[role=button]]:pointer-events-none">
          {multiline ? (
            <div style={{ whiteSpace: "pre-line" }}>
              {children ?? <span>{value}</span>}
            </div>
          ) : (
            children ?? <span>{value}</span>
          )}
        </div>
      )}
    </div>
  );
}